import { useQuery } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { useMemo } from "react";

export const useGetInviteTemplateAndUnit = ({
  applicationId,
  propertyId,
  unitId
}: {
  applicationId?: number;
  propertyId?: number;
  unitId?: number;
}) => {
  const propertyMeta = useQuery({
    queryKey: ["properties", propertyId],
    queryFn: () => addressApi.getProperty(String(propertyId) || ""),
    enabled: Boolean(propertyId && !applicationId)
  });

  const applicationMeta = useQuery({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () => addressApi.getApplicationById(String(applicationId) || ""),
    enabled: Boolean(applicationId)
  });

  // The property GET may not include the `units` array (progressive flag). In
  // that case fetch the specific unit by id directly. The cache key matches
  // other unit fetches so it's shared.
  const inlineUnit = useMemo(() => {
    if (!propertyMeta.data || !unitId) return undefined;
    return (
      propertyMeta.data.units?.find((unit) => unit.id === unitId) ??
      (propertyMeta.data.first_unit?.id === unitId
        ? propertyMeta.data.first_unit
        : undefined)
    );
  }, [propertyMeta.data, unitId]);

  const directUnitMeta = useQuery({
    queryKey: ["unit", String(unitId)],
    queryFn: () => addressApi.getUnitById(String(unitId)),
    enabled: Boolean(propertyId && !applicationId && unitId && !inlineUnit)
  });

  const templateVerificationsSettings = useMemo(() => {
    return (
      (!!applicationId ? applicationMeta : propertyMeta).data
        ?.verification_template?.settings || {}
    );
  }, [propertyMeta.data, applicationMeta.data, applicationId]);

  const unit = useMemo(() => {
    if (applicationId) return applicationMeta.data?.unit;
    if (propertyId) return inlineUnit ?? directUnitMeta.data;
    return undefined;
  }, [
    propertyId,
    applicationId,
    applicationMeta.data,
    inlineUnit,
    directUnitMeta.data
  ]);

  return { templateVerificationsSettings, unit, applicationMeta };
};
